import { useSeo } from "@/lib/seo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { FileText, Download, Printer, ChevronLeft, Gavel, RefreshCw } from "lucide-react";

const PLAN_LABELS = { base: "Privato Base", premium: "Agenzia Pro", agenzia: "Agenzia + VD Manager" };

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : "—";

export default function Invoices() {
    useSeo({ title: "Le tue fatture | VendiDirect", description: "Storico fatture e ricevute dell'abbonamento.", noindex: true });
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState({});
    const [asteStatus, setAsteStatus] = useState(null);
    const [profileForm, setProfileForm] = useState({ country: user?.country || "IT", vat_number: user?.vat_number || "", fiscal_code: user?.fiscal_code || "", billing_company: user?.billing_company || "", billing_address: user?.billing_address || "" });
    const [saving, setSaving] = useState(false);
    const [showProfile, setShowProfile] = useState(false);

    useEffect(() => {
        if (!user) { setLoading(false); return; }
        api.get("/invoices").then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLoading(false));
        api.get("/subscription/plans").then((r) => {
            const map = {};
            (r.data.plans || []).forEach((p) => { map[p.id] = p; });
            setPlans(map);
        }).catch(() => {});
        if (user.radar_aste_active) {
            api.get("/crm/aste/status").then((r) => setAsteStatus(r.data)).catch(() => {});
        }
    }, [user]);

    const saveProfile = async () => {
        setSaving(true);
        try { await api.put("/auth/profile", profileForm); setShowProfile(false); } finally { setSaving(false); }
    };

    if (!user) return <div className="pt-32 text-center text-ink-400">Accedi per vedere le fatture. <a href="/login" className="text-gold hover:underline ml-2">Login</a></div>;

    if (active) {
        return <InvoiceView invoice={active} onBack={() => setActive(null)} />;
    }

    return (
        <div data-testid="invoices-page" className="pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28">
            <div className="flex items-end justify-between mb-10">
                <div>
                    <span className="label-eyebrow">Fatturazione</span>
                    <h1 className="font-serif text-4xl text-ink-50 mt-3">Le tue fatture</h1>
                </div>
                <button data-testid="edit-billing-info" onClick={() => setShowProfile(!showProfile)} className="text-sm border border-white/20 px-5 py-2.5 text-ink-100 hover:border-gold hover:text-gold transition-colors">
                    {showProfile ? "Chiudi" : "Dati di fatturazione"}
                </button>
            </div>

            {(user.plan_id || asteStatus?.active) && (
                <BillingSummary user={user} plans={plans} asteStatus={asteStatus} />
            )}

            {showProfile && (
                <div data-testid="billing-form" className="border border-white/10 p-8 mb-10 bg-ink-900/40 space-y-4">
                    <div className="label-eyebrow mb-3">Dati per la fattura</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-ink-400 mb-2">Paese</label>
                            <select value={profileForm.country} onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })} className="w-full bg-transparent border border-white/15 px-4 py-2 text-ink-50 focus:border-gold outline-none text-sm">
                                {["IT","FR","DE","ES","PT","NL","BE","AT","IE","FI","SE","DK","GR","PL","CZ","SK","HU","RO","BG","HR","SI","LT","LV","EE","MT","CY","LU","CH","GB","US","Altro"].map((c) => <option key={c} value={c} className="bg-ink-950">{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs text-ink-400 mb-2">Ragione sociale / Nome</label>
                            <input data-testid="billing-company" value={profileForm.billing_company} onChange={(e) => setProfileForm({ ...profileForm, billing_company: e.target.value })} className="w-full bg-transparent border border-white/15 px-4 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs text-ink-400 mb-2">Partita IVA (per reverse-charge B2B UE)</label>
                            <input data-testid="billing-vat" value={profileForm.vat_number} onChange={(e) => setProfileForm({ ...profileForm, vat_number: e.target.value })} className="w-full bg-transparent border border-white/15 px-4 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs text-ink-400 mb-2">Codice fiscale</label>
                            <input data-testid="billing-cf" value={profileForm.fiscal_code} onChange={(e) => setProfileForm({ ...profileForm, fiscal_code: e.target.value })} className="w-full bg-transparent border border-white/15 px-4 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs text-ink-400 mb-2">Indirizzo</label>
                            <input value={profileForm.billing_address || ""} onChange={(e) => setProfileForm({ ...profileForm, billing_address: e.target.value })} className="w-full bg-transparent border border-white/15 px-4 py-2 text-ink-50 focus:border-gold outline-none text-sm" />
                        </div>
                    </div>
                    <button data-testid="save-billing" onClick={saveProfile} disabled={saving} className="bg-gold text-black px-6 py-2.5 text-sm font-medium hover:bg-gold-soft transition-colors">
                        {saving ? "Salvataggio..." : "Salva dati"}
                    </button>
                    <p className="text-xs text-ink-400">I dati saranno usati nella prossima fattura. Indica la P.IVA UE per applicare il reverse-charge.</p>
                </div>
            )}

            {loading ? <div className="text-ink-400">Caricamento...</div> :
             items.length === 0 ? (
                <div className="border border-white/10 p-16 text-center text-ink-400">
                    <FileText size={28} className="mx-auto mb-3 text-gold" />
                    Nessuna fattura emessa. Verrà generata automaticamente al primo pagamento.
                </div>
             ) : (
                <div className="border border-white/10 divide-y divide-white/5">
                    {items.map((inv) => (
                        <div key={inv.id} data-testid={`invoice-${inv.number}`} className="p-5 grid grid-cols-12 gap-4 items-center hover:bg-white/5 transition-colors">
                            <div className="col-span-3 font-serif text-lg text-ink-50">{inv.number}</div>
                            <div className="col-span-3 text-sm text-ink-400">{new Date(inv.issued_at).toLocaleDateString("it-IT")}</div>
                            <div className="col-span-3 text-sm text-ink-100">{inv.plan_name}</div>
                            <div className="col-span-2 font-serif text-price">{formatPrice(inv.gross)}</div>
                            <div className="col-span-1 flex justify-end gap-2">
                                <a
                                    data-testid={`pdf-${inv.number}`}
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${inv.id}/pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Scarica PDF"
                                    className="p-2 hover:text-gold text-ink-100"
                                ><Download size={14} /></a>
                                <button data-testid={`view-${inv.number}`} onClick={() => setActive(inv)} title="Anteprima" className="p-2 hover:text-gold text-ink-100"><FileText size={14} /></button>
                            </div>
                        </div>
                    ))}
                </div>
             )}
        </div>
    );
}

function BillingSummary({ user, plans, asteStatus }) {
    const plan = plans[user.plan_id];
    const asteActive = !!asteStatus?.active;
    return (
        <div data-testid="billing-summary" className="border border-white/10 p-6 mb-10 bg-ink-900/40">
            <div className="label-eyebrow mb-4">Riepilogo abbonamenti</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {user.plan_id && (
                    <div data-testid="billing-summary-plan" className="border border-white/10 p-5 flex items-center justify-between gap-4">
                        <div>
                            <div className="text-sm text-ink-50 font-medium">{plan?.name || PLAN_LABELS[user.plan_id] || user.plan_id}</div>
                            <div className="text-xs text-ink-400 mt-1.5 flex items-center gap-1.5">
                                <RefreshCw size={11} /> Piano principale · rinnovo mensile
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            {plan?.price != null && <div className="font-serif text-lg text-price">{formatPrice(plan.price)}<span className="text-xs text-ink-400 font-sans">/mese</span></div>}
                            {user.plan_expires_at && <div className="text-[11px] text-ink-400 mt-1">Prossimo rinnovo: {fmtDate(user.plan_expires_at)}</div>}
                        </div>
                    </div>
                )}
                {asteActive && (
                    <div data-testid="billing-summary-radar-aste" className="border border-gold/30 bg-gold/5 p-5 flex items-center justify-between gap-4">
                        <div>
                            <div className="text-sm text-ink-50 font-medium flex items-center gap-1.5"><Gavel size={13} className="text-gold" /> Radar Aste</div>
                            <div className="text-xs text-ink-400 mt-1.5">
                                Piano {asteStatus.interval === "year" ? "annuale" : "mensile"} · {asteStatus.auto_renew && !asteStatus.cancel_requested ? "rinnovo automatico" : "rinnovo disattivato"}
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-serif text-lg text-price">
                                {formatPrice(asteStatus.interval === "year" ? asteStatus.price_annual : asteStatus.price_monthly)}
                                <span className="text-xs text-ink-400 font-sans">{asteStatus.interval === "year" ? "/anno" : "/mese"}</span>
                            </div>
                            {asteStatus.expires_at && <div className="text-[11px] text-ink-400 mt-1">Prossimo rinnovo: {fmtDate(asteStatus.expires_at)}</div>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function InvoiceView({ invoice, onBack }) {
    return (
        <div data-testid="invoice-view" className="pt-32 pb-24 max-w-4xl mx-auto px-6">
            <div className="flex justify-between mb-6 print:hidden">
                <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-gold"><ChevronLeft size={14} /> Torna alle fatture</button>
                <div className="flex gap-2">
                    <a
                        data-testid="invoice-pdf-btn"
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-white/20 text-ink-100 hover:border-gold hover:text-gold px-6 py-2.5 text-sm flex items-center gap-2"
                    ><Download size={14} /> Scarica PDF</a>
                    <button onClick={() => window.print()} className="bg-gold text-black px-6 py-2.5 text-sm font-medium hover:bg-gold-soft flex items-center gap-2"><Printer size={14} /> Stampa</button>
                </div>
            </div>

            <div className="bg-white text-black p-12 font-sans text-sm" style={{ minHeight: "29.7cm" }}>
                <div className="flex justify-between items-start mb-10">
                    <div>
                        <div className="text-2xl font-serif">vendi<span className="text-[#D4AF6A]">direct</span>.it</div>
                        <div className="text-xs text-gray-500 mt-1 tracking-[0.2em]">LA TUA CASA. DIRETTAMENTE.</div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs uppercase tracking-widest text-gray-500">Fattura</div>
                        <div className="text-3xl font-serif">{invoice.number}</div>
                        <div className="text-sm text-gray-600 mt-1">{new Date(invoice.issued_at).toLocaleDateString("it-IT")}</div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-10 mb-12">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Da</div>
                        <div className="font-medium">{invoice.supplier.name}</div>
                        <div className="text-sm text-gray-600">{invoice.supplier.address}</div>
                        <div className="text-sm text-gray-600">P.IVA: {invoice.supplier.vat_number}</div>
                        <div className="text-sm text-gray-600">{invoice.supplier.email}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">A</div>
                        <div className="font-medium">{invoice.customer.name}</div>
                        {invoice.customer.address && <div className="text-sm text-gray-600">{invoice.customer.address}</div>}
                        <div className="text-sm text-gray-600">{invoice.customer.country}</div>
                        {invoice.customer.vat_number && <div className="text-sm text-gray-600">P.IVA: {invoice.customer.vat_number}</div>}
                        {invoice.customer.fiscal_code && <div className="text-sm text-gray-600">C.F.: {invoice.customer.fiscal_code}</div>}
                        <div className="text-sm text-gray-600">{invoice.customer.email}</div>
                    </div>
                </div>

                <table className="w-full mb-10 border-t border-gray-300">
                    <thead>
                        <tr className="border-b border-gray-300 text-xs uppercase tracking-widest text-gray-500">
                            <th className="text-left py-3">Descrizione</th>
                            <th className="text-right py-3">Imponibile</th>
                            <th className="text-right py-3">IVA</th>
                            <th className="text-right py-3">Totale</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-gray-200">
                            <td className="py-4">{invoice.kind === "lead_package" ? `${invoice.plan_name} — credito immediato` : `Abbonamento ${invoice.plan_name} — ${invoice.billing_period || "1 mese"}`}</td>
                            <td className="py-4 text-right">€ {invoice.net.toFixed(2)}</td>
                            <td className="py-4 text-right">€ {invoice.vat.toFixed(2)}</td>
                            <td className="py-4 text-right">€ {invoice.gross.toFixed(2)}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="flex justify-end">
                    <div className="w-72 space-y-2 text-sm">
                        <div className="flex justify-between text-gray-600"><span>Imponibile</span><span>€ {invoice.net.toFixed(2)}</span></div>
                        <div className="flex justify-between text-gray-600"><span>{invoice.vat_label}</span><span>€ {invoice.vat.toFixed(2)}</span></div>
                        <div className="flex justify-between text-lg font-medium border-t border-gray-300 pt-2"><span>Totale</span><span>€ {invoice.gross.toFixed(2)}</span></div>
                    </div>
                </div>

                <div className="mt-16 text-xs text-gray-500 border-t border-gray-200 pt-6">
                    Pagamento eseguito tramite Stripe. Documento generato automaticamente da vendidirect.it. {invoice.vat_rate === 0 && "Operazione in regime di reverse-charge / esente IVA."}
                </div>
            </div>
        </div>
    );
}
